import { Helmet } from "react-helmet-async";

interface PageHeadProps {
  title: string;
  description: string;
  /** Primary OG image. Absolute URL preferred; relative paths are auto-resolved to https://queebi.com. */
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  /** Optional smaller (600x315) fallback emitted as a secondary og:image. */
  imageSmall?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
}

const BASE_URL = "https://queebi.com";
const OG_VERSION = "20260624";
const DEFAULT_IMAGE = `${BASE_URL}/images/og/default-1200x630.jpg`;
const DEFAULT_IMAGE_SMALL = `${BASE_URL}/images/og/default-600x315.jpg`;
const DEFAULT_IMAGE_ALT = "Queebi Land — Helping Little Hearts Understand Big Feelings";

function toAbsolute(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function withVersion(url: string): string {
  if (url.includes("?")) return url;
  return `${url}?v=${OG_VERSION}`;
}

const PageHead = ({
  title,
  description,
  image,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  imageSmall,
  keywords,
  canonical,
  ogType = "website",
}: PageHeadProps) => {
  const primary = withVersion(toAbsolute(image || DEFAULT_IMAGE));
  const small = imageSmall ? withVersion(toAbsolute(imageSmall)) : !image ? withVersion(DEFAULT_IMAGE_SMALL) : null;
  const alt = imageAlt || DEFAULT_IMAGE_ALT;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {canonical && <meta property="og:url" content={canonical} />}

      <meta property="og:image" content={primary} />
      <meta property="og:image:secure_url" content={primary} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={alt} />

      {small && <meta property="og:image" content={small} />}
      {small && <meta property="og:image:width" content="600" />}
      {small && <meta property="og:image:height" content="315" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={primary} />
      <meta name="twitter:image:alt" content={alt} />
    </Helmet>
  );
};

export default PageHead;
